import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import type { ChangeOrder, ClientView, DecideBody, Lang } from '@buildiary/core';
import { useLang, useT } from '../app/context';
import { LANG_NAME, LANGS } from '../i18n';
import { ApiFailure, request, setPublicLink } from '../lib/api';
import { compact, day, firstName, money, shortDay, signed, time } from '../lib/format';
import { waLink } from '../lib/share';
import { useAsync } from '../lib/useAsync';
import { BrandTile, Icon, Wordmark } from '../ui/Icon';
import { Button, Chip, Dialog, EmptyState, Field, Loading, Photo, Textarea, useToast } from '../ui/kit';

/** Progress ring from the MobOwner board. */
export function Ring({ pct, size = 84 }: { pct: number; size?: number }) {
  const t = useT();
  const r = 34;
  const c = 2 * Math.PI * r;
  const on = (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <svg width={size} height={size} viewBox="0 0 84 84" role="img" aria-label={t('owner.built', { n: Math.round(pct) })} style={{ flex: 'none' }}>
      <circle cx="42" cy="42" r={r} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="10" />
      <circle cx="42" cy="42" r={r} fill="none" stroke="var(--brand-300)" strokeWidth="10" strokeLinecap="round" strokeDasharray={`${on} ${c}`} transform="rotate(-90 42 42)" />
      <text x="42" y="48" textAnchor="middle" fill="#FFFFFF" fontSize="18" fontWeight="600" fontFamily="IBM Plex Sans, sans-serif">{Math.round(pct)}%</text>
    </svg>
  );
}

/** What the owner sees: on their phone (signed in) and through the client link (no sign-in). MobOwner board. */
export function OwnerView({ view, decide, full }: { view: ClientView; decide: (co: ChangeOrder, body: DecideBody) => Promise<void>; full?: boolean }) {
  const t = useT();
  const { lang: uiLang } = useLang();
  const [lang, setLang] = useState<Lang>(uiLang);
  const [showAll, setShowAll] = useState(false);
  const [deciding, setDeciding] = useState<{ co: ChangeOrder; decision: 'approve' | 'decline' } | null>(null);
  const p = view.project;
  const cur = p.currency;
  const waiting = view.changeOrders.filter((c) => c.status === 'waiting');
  const builder = firstName(view.changeOrders[0]?.raisedBy ?? view.workspaceName);
  const nextDue = view.invoices.find((i) => i.status === 'due') ?? view.invoices.find((i) => i.status === 'not_yet');
  const diff = view.plannedThisWeek - view.spentThisWeek;
  const w = view.weeklyUpdate;
  const shown = showAll ? waiting : waiting.slice(0, 1);
  return (
    <div className="stack-lg" style={{ gap: 12 }}>
      <section className="hero-dark">
        <Ring pct={p.progressPct} />
        <div className="stack" style={{ gap: 4 }}>
          {/* One week everywhere: the update's own week, which the cards below use too. */}
          <span className="k">{t('owner.week', { week: view.week.label })}{w?.sentAt ? ` · ${t('owner.sentOn', { when: shortDay(w.sentAt, uiLang) })}` : ''}</span>
          <span className="h">{t('client.stage', { stage: p.stage })}</span>
          {p.plannedEnd && <span className="s">{t('owner.handover', { date: day(p.plannedEnd, uiLang) })}</span>}
        </div>
      </section>

      <section className="card" style={{ padding: 14, gap: 10 }}>
        <div className="row between">
          <h2 style={{ fontSize: 16 }}>{t('owner.from', { name: builder })}</h2>
          <div className="mini-lang" role="group" aria-label={t('top.language')}>
            {LANGS.map((l) => (
              <button key={l} type="button" lang={l} className={l} aria-pressed={lang === l} onClick={() => setLang(l)}>
                {l === 'en' ? 'EN' : l === 'ur' ? 'اردو' : 'عربي'}
              </button>
            ))}
          </div>
        </div>
        {w ? (
          <p lang={lang} dir={lang === 'en' ? 'ltr' : 'rtl'} style={{ fontFamily: lang === 'ur' ? "'Noto Nastaliq Urdu', 'IBM Plex Sans', serif" : lang === 'ar' ? "'IBM Plex Sans Arabic', 'IBM Plex Sans', sans-serif" : undefined, fontSize: lang === 'ur' ? 15 : 14, lineHeight: lang === 'ur' ? 2.1 : 1.6, color: 'var(--ink-2)' }}>
            {w.text[lang]}
          </p>
        ) : (
          <p className="muted">{t('owner.noUpdate')}</p>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6 }}>
          {view.photos.slice(0, 3).map((ph) => (
            <Photo key={ph.id} id={ph.id} label={ph.caption} height={64} />
          ))}
          {view.photos.length > 3 && (
            <div className="ph" style={{ height: 64, background: 'var(--brand-300)', color: 'var(--brand-900)', fontWeight: 600, fontSize: 13 }}>+{(w?.photoCount ?? view.photos.length) - 3}</div>
          )}
        </div>
      </section>

      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
        <div className="card" style={{ padding: 12, gap: 4 }}>
          <span className="xsmall muted">{t('owner.spent')}</span>
          <span className="num" style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink)' }}>{money(view.spentThisWeek, cur, { compact: true })}</span>
          <span className={`xsmall ${diff >= 0 ? 'success-text' : 'danger-text'}`}>{diff >= 0 ? t('owner.under', { amt: compact(diff) }) : t('owner.over', { amt: compact(-diff) })}</span>
        </div>
        <div className="card" style={{ padding: 12, gap: 4 }}>
          <span className="xsmall muted">{t('owner.nextPay')}</span>
          <span className="num" style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink)' }}>{nextDue ? money(nextDue.amount, cur, { compact: true }) : '—'}</span>
          <span className="xsmall warning-text">{nextDue ? t('owner.stageDue', { n: nextDue.stageSeq, date: shortDay(nextDue.dueDate, uiLang) }) : t('owner.nothingDue')}</span>
        </div>
      </section>

      <section className="card accent" style={{ padding: 14, gap: 10 }}>
        <div className="row between">
          <h2 style={{ fontSize: 16 }}>{t('owner.needsYes')}</h2>
          <span className="count-pill">{waiting.length}</span>
        </div>
        {waiting.length === 0 && <p className="muted small">{t('owner.allDone')}</p>}
        {shown.map((co) => (
          <div key={co.id} className="stack" style={{ gap: 10, paddingBottom: 4 }}>
            <div className="stack" style={{ gap: 2 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{co.title}</span>
              <span className="small muted">
                <span className="latin">{co.number}</span> · <span className="num">{money(co.amount, cur)}</span> · {co.days ? t('owner.days', { n: co.days }) : t('owner.noDays')}
              </span>
            </div>
            {full && (
              <div className="stack" style={{ gap: 4 }}>
                <span className="section-label">{t('client.lines')}</span>
                {co.lines.map((l, i) => (
                  <div key={i} className="row between small" style={{ gap: 12 }}>
                    <span className="grow">{l.description}</span>
                    <span className="mono nowrap">{signed(l.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="row" style={{ gap: 8 }}>
              <a className="btn" style={{ flex: '1 1 0', minHeight: 44 }} href={waLink(`About ${co.number}, ${co.title}: `)} target="_blank" rel="noreferrer">{t('owner.ask', { name: builder })}</a>
              <Button variant="primary" style={{ flex: '1 1 0', minHeight: 44 }} onClick={() => setDeciding({ co, decision: 'approve' })}>{t('c.approve')}</Button>
            </div>
            {full && (
              <button type="button" className="link-btn danger-text" style={{ alignSelf: 'flex-start', minHeight: 36 }} onClick={() => setDeciding({ co, decision: 'decline' })}>{t('c.decline')}</button>
            )}
          </div>
        ))}
        {!showAll && waiting.length > 1 && (
          <button type="button" className="link-btn" style={{ alignSelf: 'flex-start', fontSize: 13 }} onClick={() => setShowAll(true)}>
            {t('owner.more', { n: waiting.length - 1, title: waiting[1]!.title.toLowerCase(), amt: money(waiting[1]!.amount, cur) })} →
          </button>
        )}
      </section>

      {full && (
        <>
          <section className="card" style={{ gap: 0 }}>
            <div className="card-head"><h2 className="card-title">{t('owner.invoices')}</h2></div>
            {view.invoices.map((i) => (
              <div key={i.id} className="list-row" style={{ padding: '10px 16px' }}>
                <span className="grow" style={{ fontSize: 14 }}><span className="num">{i.stageSeq}</span> · {i.title}</span>
                <span className="mono xsmall">{compact(i.amount)}</span>
                <Chip size="sm" tone={i.status === 'paid' ? 'success' : i.status === 'due' ? 'warning' : 'neutral'}>{i.status === 'paid' ? t('owner.paid') : i.status === 'due' ? t('owner.due', { date: shortDay(i.dueDate, uiLang) }) : t('owner.notYet')}</Chip>
              </div>
            ))}
          </section>
          <section className="card" style={{ gap: 0 }}>
            <div className="card-head"><h2 className="card-title">{t('owner.changes')}</h2></div>
            {view.changeOrders.map((c) => (
              <div key={c.id} className="list-row" style={{ padding: '10px 16px' }}>
                <span className="mono xsmall muted" style={{ width: 52 }}>{c.number}</span>
                <span className="grow ellipsis" style={{ fontSize: 14 }}>{c.title}</span>
                <span className="mono xsmall">{signed(c.amount)}</span>
                <Chip size="sm" tone={c.status === 'approved' ? 'success' : c.status === 'declined' ? 'danger' : 'brand'}>{c.status === 'approved' ? t('owner.approved') : c.status === 'declined' ? t('owner.declined') : t('owner.waiting')}</Chip>
              </div>
            ))}
          </section>
          <section className="card pad">
            <h2 className="card-title">{t('owner.photos')}</h2>
            <div className="photo-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
              {view.photos.map((ph) => (
                <div key={ph.id} className="photo-card">
                  <Photo id={ph.id} label={ph.caption} height={96} />
                  <span className="xsmall muted">{ph.caption} · {shortDay(ph.at, uiLang)} <span className="mono">{time(ph.at)}</span></span>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {deciding && <DecideDialog co={deciding.co} decision={deciding.decision} currency={cur} onClose={() => setDeciding(null)} onDecide={async (b) => { await decide(deciding.co, b); setDeciding(null); }} />}
    </div>
  );
}

function DecideDialog({ co, decision, currency, onClose, onDecide }: { co: ChangeOrder; decision: 'approve' | 'decline'; currency: string; onClose: () => void; onDecide: (b: DecideBody) => Promise<void> }) {
  const t = useT();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <Dialog
      title={decision === 'approve' ? t('owner.confirmApprove', { number: co.number, amt: money(co.amount, currency) }) : t('owner.confirmDecline', { number: co.number })}
      sub={co.title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('c.cancel')}</Button>
          <Button
            variant={decision === 'approve' ? 'primary' : 'danger'}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await onDecide({ decision, note: note.trim() || undefined });
              } catch (e) {
                setErr(e instanceof ApiFailure ? e.body.message : t('c.error'));
                setBusy(false);
              }
            }}
          >
            {decision === 'approve' ? t('c.approve') : t('c.decline')}
          </Button>
        </>
      }
    >
      <Field label={`${t('owner.note')} (${t('c.optional')})`}>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
      </Field>
      {err && <div className="alert danger" role="alert">{err}</div>}
    </Dialog>
  );
}

function usePublicLink(token: string) {
  // set before the first render of photos, cleared when leaving the page
  setPublicLink(token);
  useEffect(() => () => setPublicLink(null), [token]);
}

function PublicFrame({ children, title }: { children: React.ReactNode; title?: string }) {
  const { lang, setLang } = useLang();
  const t = useT();
  return (
    <div className="public">
      <header className="public-head">
        <BrandTile size={32} />
        <Wordmark width={96} />
        {title && <span style={{ fontSize: 14, color: 'rgba(255,255,255,.8)', marginInlineStart: 8 }}>{title}</span>}
        <div className="hero-lang" role="group" aria-label={t('top.language')} style={{ marginInlineStart: 'auto' }}>
          {LANGS.map((l) => (
            <button key={l} type="button" lang={l} aria-pressed={lang === l} onClick={() => setLang(l)} style={{ fontFamily: l === 'ur' ? "'Noto Nastaliq Urdu', serif" : l === 'ar' ? "'IBM Plex Sans Arabic', sans-serif" : 'var(--font-sans)' }}>
              {LANG_NAME[l]}
            </button>
          ))}
        </div>
      </header>
      <main className="public-body">{children}</main>
      <footer className="public-foot">Buildiary · {t('si.endorse')}</footer>
    </div>
  );
}

/** /c/:token — the owner's link: progress, photos, invoices, change orders to approve. No sign-in. */
export function ClientLinkPage() {
  const { token = '' } = useParams();
  usePublicLink(token);
  const t = useT();
  const toast = useToast();
  const v = useAsync(() => request('GET /api/c/:token', { params: { token } }), [token]);
  return (
    <PublicFrame title={v.data?.project.name}>
      {v.loading && !v.data && <Loading rows={8} />}
      {v.error != null && !v.data && <EmptyState icon="lock" title={t('client.bad')} />}
      {v.data && (
        <>
          <div className="stack" style={{ gap: 4 }}>
            <span className="eyebrow">{t('owner.house')}</span>
            <h1 className="page-title" style={{ fontSize: 30 }}>{v.data.project.name}, <span className="latin">{v.data.project.place}</span></h1>
            <span className="muted small">{t('client.shared', { workspace: v.data.workspaceName })} · {t('client.private')}</span>
          </div>
          <OwnerView
            full
            view={v.data}
            decide={async (co, body) => {
              const updated = await request('POST /api/c/:token/change-orders/:id/decide', { params: { token, id: co.id }, body });
              v.set({ ...v.data!, changeOrders: v.data!.changeOrders.map((c) => (c.id === updated.id ? updated : c)) });
              toast(t('owner.thanks'));
            }}
          />
        </>
      )}
    </PublicFrame>
  );
}

/** /s/:token — the supplier's statement: orders, bills, payments and balance. No sign-in. */
export function SupplierLinkPage() {
  const { token = '' } = useParams();
  usePublicLink(token);
  const t = useT();
  const { lang } = useLang();
  const v = useAsync(() => request('GET /api/s/:token', { params: { token } }), [token]);
  return (
    <PublicFrame title={v.data ? t('supplier.title', { name: v.data.supplier.name }) : undefined}>
      {v.loading && !v.data && <Loading rows={8} />}
      {v.error != null && !v.data && <EmptyState icon="lock" title={t('client.bad')} />}
      {v.data && (
        <>
          <div className="stack" style={{ gap: 4 }}>
            <span className="eyebrow">{t('supplier.from', { workspace: v.data.workspaceName })}</span>
            <h1 className="page-title" style={{ fontSize: 30 }}>{t('supplier.title', { name: v.data.supplier.name })}</h1>
            <span className="muted small">{t('client.private')}</span>
          </div>
          <div className="tiles wide">
            <div className="tile"><span className="tile-title">{t('supplier.balance')}</span><span className="tile-value">{money(v.data.balance, v.data.currency)}</span></div>
            <div className="tile"><span className="tile-title">{t('supplier.orders')}</span><span className="tile-value">{v.data.orders.length}</span></div>
            <div className="tile"><span className="tile-title">{t('supplier.bills')}</span><span className="tile-value">{v.data.bills.length}</span></div>
          </div>
          <section className="card">
            <div className="card-head"><h2 className="card-title">{t('supplier.orders')}</h2></div>
            {v.data.orders.length === 0 && <EmptyState title={t('c.empty')} />}
            {v.data.orders.map((o) => (
              <details key={o.id} className="list-row" style={{ display: 'block' }}>
                <summary className="row" style={{ cursor: 'pointer', minHeight: 32 }}>
                  <span className="mono small">{o.number}</span>
                  <span className="grow muted small">{day(o.createdAt, lang)}</span>
                  <span className="mono small">{money(o.total, v.data!.currency)}</span>
                  <Chip size="sm" tone={o.status === 'received' || o.status === 'closed' ? 'success' : 'brand'}>{o.status.replace('_', ' ')}</Chip>
                </summary>
                <table className="table" style={{ marginTop: 8 }}>
                  <thead><tr><th>Item</th><th className="r">Ordered</th><th className="r">Received</th><th className="r">Rate</th><th className="r">Amount</th></tr></thead>
                  <tbody>
                    {o.lines.map((l) => (
                      <tr key={l.itemId}><td>{l.itemName}</td><td className="r mono">{l.qty} {l.unit}</td><td className="r mono">{l.received}</td><td className="r mono">{compact(l.rate)}</td><td className="r mono">{compact(l.amount)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </details>
            ))}
          </section>
          <div className="grid-2">
            <section className="card">
              <div className="card-head"><h2 className="card-title">{t('supplier.bills')}</h2></div>
              {v.data.bills.length === 0 && <EmptyState title={t('c.empty')} />}
              {v.data.bills.map((b) => (
                <div key={b.id} className="list-row">
                  <span className="mono small">{b.number}</span>
                  <span className="grow muted small">{day(b.date, lang)}</span>
                  <span className="mono small">{money(b.amount, v.data!.currency)}</span>
                  <Chip size="sm" tone={b.status === 'paid' ? 'success' : b.status === 'approved' ? 'brand' : 'neutral'}>{b.status === 'to_check' ? 'being checked' : b.status}</Chip>
                </div>
              ))}
            </section>
            <section className="card">
              <div className="card-head"><h2 className="card-title">{t('supplier.payments')}</h2></div>
              {v.data.payments.length === 0 && <EmptyState title={t('c.empty')} />}
              {v.data.payments.map((p) => (
                <div key={p.id} className="list-row">
                  <Icon name="money" size={16} />
                  <span className="grow small">{day(p.at, lang)} · {p.method} · <span className="mono">{p.reference}</span></span>
                  <span className="mono small">{money(p.amount, v.data!.currency)}</span>
                </div>
              ))}
            </section>
          </div>
        </>
      )}
    </PublicFrame>
  );
}
